import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, PHOTO_GRADE } from "./types";

// venueCost — a wedding cost guide / budget breakdown over a full-bleed backdrop
// photo. Line items sit in a centered semi-transparent white card with a thin
// bronze line frame inset. A blue script title, then line items with a dotted
// leader to a right-aligned price, plus an optional highlighted total row.
//   imageUrl           → backdrop photo (optional; falls back to a warm tone)
//   venue.name / title → script title
//   items[]            → { name: line item, caption: price range }
//   subtitle           → optional estimated-total amount
const BRONZE = "rgba(176,141,87,.8)";
const LINE = "rgba(74,95,134,.3)";
const LEAD = "rgba(74,95,134,.5)";
const FALLBACK_BG = "#e7ddcb";

export function venueCostTemplate(input: PinInput): string {
  const { brand, typography, venue, items, subtitle, imageUrl } = input;
  const title = (venue?.name || input.title || "").trim();
  const rows = (items ?? []).filter((i) => i.name).slice(0, 12);
  if (!rows.length) throw new Error("venueCost template requires items[] with names");

  // Region/context the figures are for (eyebrow overrides; defaults to GTA).
  const region = (input.eyebrow || "").trim() || "Greater Toronto Area · 2026";

  // Title scales DOWN as it gets longer so it always fits the 2-line clamp
  // without truncating ("…").
  const ttl = title.length;
  const titleSize = ttl <= 22 ? 98 : ttl <= 32 ? 86 : ttl <= 44 ? 74 : ttl <= 56 ? 64 : 56;
  const ink = input.textColor || "#141414";
  const n = rows.length;
  // Item font: a row-count ceiling, then capped so the WIDEST row (longest label
  // + its price) fits the card width — so labels never truncate ("Photography &
  // vi…"). ~0.49em/char measured for EB Garamond + ~1em for the leader/margins.
  const byCount = n <= 7 ? 60 : n <= 9 ? 52 : n <= 11 ? 44 : 38;
  const widestEm = Math.max(...rows.map((r) => (r.name.length + (r.caption || "").length) * 0.49), 1) + 1.0;
  const byRow = Math.floor(752 / widestEm);
  const itemFont = Math.max(32, Math.min(byCount, byRow));
  // Tighter row padding so a larger title leaves the list room (the .items box
  // uses space-evenly, so rows also auto-compress as the title grows).
  const rowPad = n <= 7 ? 9 : n <= 9 ? 7 : n <= 11 ? 5 : 4;

  const rowsHtml = rows.map((r) => `
    <div class="row"><span class="label">${escapeHtml(r.name)}</span><span class="leader"></span><span class="price">${escapeHtml(r.caption || "")}</span></div>`).join("");

  const totalHtml = subtitle && subtitle.trim()
    ? `<div class="total"><span class="tlabel">Estimated Total</span><span class="tprice">${escapeHtml(subtitle.trim())}</span></div>`
    : "";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=EB+Garamond:wght@400;500;600;700&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:'EB Garamond',${typography.bodyFont},serif;-webkit-font-smoothing:antialiased;background:${imageUrl ? "#2b2520" : FALLBACK_BG}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${imageUrl ? "#2b2520" : FALLBACK_BG}}
  ${imageUrl ? `.photo{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:center;filter:${PHOTO_GRADE}}
  .photo::after{content:"";position:absolute;inset:0;background:rgba(0,0,0,.18)}` : ""}
  .card{position:absolute;inset:56px;z-index:2;background:rgba(255,255,255,.9);display:flex;flex-direction:column;padding:64px 58px 56px;box-shadow:0 18px 60px rgba(0,0,0,.3)}
  .card::before{content:"";position:absolute;inset:18px;border:1.5px solid ${BRONZE};pointer-events:none}
  .title{font-family:'Bebas Neue',sans-serif;font-size:${titleSize}px;line-height:.98;letter-spacing:2px;text-transform:uppercase;color:${ink};text-align:center;padding-bottom:.04em;flex-shrink:0;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .tdiv{width:200px;height:1px;background:${LINE};margin:10px auto 0;flex-shrink:0}
  .region{text-align:center;font-family:'EB Garamond',serif;font-size:25px;letter-spacing:3px;text-transform:uppercase;color:${ink};opacity:.72;margin-top:10px;flex-shrink:0}
  .items{flex:1;display:flex;flex-direction:column;justify-content:space-evenly;padding:18px 6px 0}
  .row{display:flex;align-items:baseline;padding:${rowPad}px 0}
  .label{font-size:${itemFont}px;color:${ink};white-space:nowrap;flex-shrink:0;max-width:74%;overflow:hidden;text-overflow:ellipsis}
  .leader{flex:1;border-bottom:2px dotted ${LEAD};margin:0 12px;transform:translateY(-.28em)}
  .price{font-size:${itemFont}px;font-weight:600;color:${ink};white-space:nowrap;flex-shrink:0}
  .total{display:flex;justify-content:space-between;align-items:baseline;gap:18px;border-top:2px solid ${LINE};margin:10px 6px 0;padding-top:16px;flex-shrink:0}
  .tlabel{font-family:'EB Garamond',serif;font-weight:600;font-size:${Math.round(itemFont * 0.62)}px;letter-spacing:1px;text-transform:uppercase;color:${ink};white-space:nowrap}
  .tprice{font-family:'EB Garamond',serif;font-weight:700;font-size:${Math.round(itemFont * 0.8)}px;color:${ink};white-space:nowrap}
  .brandline{text-align:center;font-family:'EB Garamond',serif;font-size:24px;letter-spacing:4px;text-transform:uppercase;color:${ink};opacity:.72;padding-top:30px;flex-shrink:0}
</style></head>
<body><div class="pin">
  ${imageUrl ? `<div class="photo"></div>` : ""}
  <div class="card">
    ${title ? `<div class="title">${escapeHtml(title)}</div><div class="tdiv"></div>` : ""}
    <div class="region">${escapeHtml(region)}</div>
    <div class="items">${rowsHtml}</div>
    ${totalHtml}
    <div class="brandline">${escapeHtml(brand || "Sauntera Weddings")}</div>
  </div>
</div></body></html>`;
}
